import React from "react";
import * as Icon from "react-feather";
import { toast as alert } from "react-toastify";

import BookkeepingService from "../../../../services/BookkeepingService";
import WComponent from "../../../common/WComponent";
import ToolsModal from "../kit/ToolsModal";
import ToolsSelect from "../kit/ToolsSelect";
import CustomTaxRateRow, { CUSTOM_RATE_OPTION_VALUE } from "./CustomTaxRateRow";

/**
 * Adding an account to a client's chart, wherever it is asked for.
 *
 * Two screens ask: the transactions table, and the statements screen when a bank account has
 * nothing to post to. Each used to run its own create, read for itself the several shapes the
 * endpoint answers in, and word its own success and failure, which is how one of them came to
 * create an account under an invented number while the client's own ledger already had one for
 * it. The account is created here, once, and the caller is handed what was created so it can do
 * whatever it needed the account for.
 *
 * The form stays locked from the moment Add is pressed until the caller has finished with the
 * account it was handed. The caller's work is a round trip of its own, and a button that comes
 * back to life during it takes a second click as a second account: the number field is usually
 * left blank, so the server assigns a different one and a real duplicate appears rather than a
 * refusal. The modal closes itself for the same reason, so no caller has to remember to.
 */
class AddChartAccountModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			name: props.initialName || "",
			accountNumber: props.initialAccountNumber || "",
			canonicalCode: "",
			tax: "",
			customRateOpen: false,
			saving: false,
		};
	}

	canAdd = () => {
		const hasName = this.state.name.trim() !== "";
		const hasStatementLine = this.state.canonicalCode !== "";
		return hasName && hasStatementLine && !this.state.saving;
	};

	onTaxSelect = async selectedTax => {
		if (selectedTax === CUSTOM_RATE_OPTION_VALUE) {
			await this.update({ customRateOpen: true });
			return;
		}
		await this.update({ tax: selectedTax });
	};

	onCustomRateApplied = async ({ option }) => {
		await this.update({ tax: option, customRateOpen: false });
	};

	onCancelCustomRate = () => this.update({ customRateOpen: false });

	onAddClicked = async () => {
		const { clientId, onCreated, onClose } = this.props;
		if (!this.canAdd()) return;

		await this.update({ saving: true });
		const outcome = await BookkeepingService.addAccountToTheChart({
			clientId,
			accountName: this.state.name.trim(),
			accountNumber: this.state.accountNumber.trim() || null,
			canonicalCode: this.state.canonicalCode,
		});

		if (outcome.error) {
			await this.update({ saving: false });
			alert.error(outcome.error);
			return;
		}

		alert.success(`${outcome.account.accountName} was added to the chart of accounts.`);
		try {
			await onCreated({ account: outcome.account, tax: this.state.tax });
		} finally {
			onClose();
		}
	};

	renderFooter = () => {
		const { onClose } = this.props;

		return (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" onClick={this.onAddClicked} disabled={!this.canAdd()}>
						<Icon.Plus size={16} />
						{this.state.saving ? "Adding…" : "Add account"}
					</button>
				</span>
			</>
		);
	};

	renderTaxDefault = () => {
		const { taxOptions, clientId, customTaxRates, onCustomTaxRatesChange } = this.props;
		const taxChoices = [
			{ value: "", label: "— No default —" },
			...(taxOptions || []).map(taxOption => ({ value: taxOption, label: taxOption })),
			{ value: CUSTOM_RATE_OPTION_VALUE, label: "+ Custom rate…" },
		];

		return (
			<div className="tk-form-field">
				<div className="tk-form-label">Default tax</div>
				<ToolsSelect
					value={this.state.tax}
					options={taxChoices}
					onChange={this.onTaxSelect}
					placeholder="— No default —"
				/>
				{(taxOptions || []).length === 0 && <div className="tk-form-help">Select a province in Settings to see tax options.</div>}
				{this.state.customRateOpen && (
					<CustomTaxRateRow
						clientId={clientId}
						taxOptions={taxOptions}
						customTaxRates={customTaxRates}
						onCustomTaxRatesChange={onCustomTaxRatesChange}
						onApply={this.onCustomRateApplied}
						onCancel={this.onCancelCustomRate}
					/>
				)}
			</div>
		);
	};

	render() {
		const { canonicalOptions, onClose, showTaxDefault = true } = this.props;
		const { name, accountNumber, canonicalCode } = this.state;

		const statementLineChoices = (canonicalOptions || []).map(option => ({ value: option.canonical_code, label: option.display_name }));

		return (
			<ToolsModal open={true} onClose={onClose} title="Add chart account" width={440} footer={this.renderFooter()}>
				<div className="tk-form-field">
					<div className="tk-form-label">Account name <span className="tk-form-required">*</span></div>
					<input
						className="tk-input"
						value={name}
						onChange={e => this.update({ name: e.target.value })}
						placeholder="e.g. Office Supplies"
						autoFocus
					/>
				</div>
				<div className="tk-form-field">
					<div className="tk-form-label">Account number</div>
					<input
						className="tk-input"
						value={accountNumber}
						onChange={e => this.update({ accountNumber: e.target.value })}
						placeholder="Assigned automatically when left blank"
					/>
					<div className="tk-form-help">Use the number the client's own ledger gives this account, so the two are recognised as one.</div>
				</div>
				<div className="tk-form-field">
					<div className="tk-form-label">Reports on <span className="tk-form-required">*</span></div>
					<ToolsSelect
						value={canonicalCode}
						options={statementLineChoices}
						onChange={chosenCode => this.update({ canonicalCode: chosenCode })}
						placeholder="Select the financial statement line…"
						searchable
						searchPlaceholder="Search statement lines…"
					/>
					<div className="tk-form-help">The line this account rolls up into on the financial statements.</div>
				</div>
				{showTaxDefault && this.renderTaxDefault()}
			</ToolsModal>
		);
	}
}

export default AddChartAccountModal;
